import styled, { css } from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { UseFormSetValue } from "react-hook-form";
import { FC, useEffect, useState } from "react";

import Text from "@/components/Text";
import PulsingLoader from "@/components/PulsingLoader";
import { api } from "@/utils/api";
import { FormDataT } from "@/pages/ProductionEvent";
import { facebookRooms, youtubeRooms } from "@/utils/data/productionEvents";
import { EventsStatusEnum } from "@/utils/enums";

const Box = styled.div`
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 24px;
`;

const RoomsBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const BorderBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  padding: 16px;
`;

const SectionHeading = styled(Text)`
  margin-bottom: 8px;
`;

const RoomButton = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.smallBold.fontWeight};
    font-size: ${theme.typography.smallBold.fontSize};
    line-height: ${theme.typography.smallBold.lineHeight};
  `}
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.success};
  height: 40px;
  padding: 0 16px;
  color: ${({ theme }) => theme.colors.success};
  transition: all ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.success};
  }
  ${({ $active, theme }) =>
    $active &&
    css`
      background-color: ${theme.colors.success};
      color: ${theme.colors.white};
    `}
  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      background-color: ${theme.colors.grayscaleLight};
      color: ${theme.colors.gray};
      border-color: transparent;
      cursor: not-allowed;
      &:hover {
        background-color: ${theme.colors.grayscaleLight};
        color: ${theme.colors.gray};
      }
    `}
`;

const NoLocations = styled(Text)`
  text-align: center;
  padding: 51px 0;
`;

const LoaderHolder = styled.div`
  text-align: center;
  padding: 19px 0 20px;
`;

const LoaderText = styled(Text)`
  margin-top: 4px;
`;

interface Props {
  eventStartsAt: string;
  eventDuration: number;
  locationType: string | null | undefined;
  setValue: UseFormSetValue<FormDataT>;
  meetingRoomSelected?: boolean;
  meetingRoom?: string;
  fieldIndex: number;
  eventStatus: EventsStatusEnum;
}

const MeetingRooms: FC<Props> = ({
  eventStartsAt,
  eventDuration,
  locationType,
  setValue,
  meetingRoomSelected,
  meetingRoom,
  fieldIndex,
  eventStatus,
}) => {
  const [areRoomsSelectable, setAreRoomsSelectable] = useState(false);

  const [activeRoom, setActiveRoom] = useState("");

  const fetchRooms = () =>
    api.post<never, { availableRooms: string[] }>(
      "/v1/production/meeting-rooms-availability",
      {
        starts_at: eventStartsAt,
        duration: eventDuration,
        type: locationType,
      }
    );

  const {
    data: meetingRooms,
    isFetching,
    refetch,
  } = useQuery<{ availableRooms: string[] }>(
    ["meeting_rooms", { eventStartsAt, eventDuration, locationType }],
    fetchRooms,
    {
      enabled: false,
      onSuccess: () => {
        setAreRoomsSelectable(true);
      },
    }
  );

  useEffect(() => {
    if (
      !locationType ||
      !eventStartsAt ||
      !eventDuration ||
      (meetingRoom && eventStatus === EventsStatusEnum.SENT)
    ) {
      return setAreRoomsSelectable(false);
    }
    setActiveRoom("");
    setValue(`events.${fieldIndex}.meeting_room`, null);
    refetch();
  }, [
    eventStartsAt,
    eventDuration,
    locationType,
    refetch,
    fieldIndex,
    setValue,
    eventStatus,
    meetingRoom,
  ]);

  return (
    <Box>
      <SectionHeading $type="caption">Meeting Rooms</SectionHeading>
      <BorderBlock>
        {eventStatus === EventsStatusEnum.SENT ? (
          !!meetingRoom ? (
            <RoomsBlock>
              <RoomButton $disabled>{meetingRoom} room</RoomButton>
            </RoomsBlock>
          ) : (
            <NoLocations $type="button" $color="gray">
              No location selected
            </NoLocations>
          )
        ) : !!isFetching ? (
          <LoaderHolder>
            <PulsingLoader />
            <LoaderText $type="button">Loading meeting rooms ...</LoaderText>
          </LoaderHolder>
        ) : (
          <>
            {!!areRoomsSelectable ? (
              <RoomsBlock>
                {locationType === "facebook"
                  ? facebookRooms.map((room, index) => {
                      return (
                        <RoomButton
                          $active={activeRoom === room}
                          $disabled={
                            !meetingRooms?.availableRooms.includes(room)
                          }
                          onClick={() => {
                            setActiveRoom(room);
                            setValue(`events.${fieldIndex}.meeting_room`, room);
                          }}
                          key={index}
                        >
                          {room} room
                        </RoomButton>
                      );
                    })
                  : youtubeRooms.map((room, index) => {
                      return (
                        <RoomButton
                          $active={activeRoom === room}
                          onClick={() => {
                            setActiveRoom(room);
                            setValue(`events.${fieldIndex}.meeting_room`, room);
                          }}
                          $disabled={
                            !meetingRooms?.availableRooms.includes(room)
                          }
                          key={index}
                        >
                          {room} room
                        </RoomButton>
                      );
                    })}
              </RoomsBlock>
            ) : (
              <NoLocations $type="button" $color="gray">
                No location selected
              </NoLocations>
            )}
          </>
        )}
      </BorderBlock>
    </Box>
  );
};

export default MeetingRooms;
